// Linear progression (used by split templates, see planner.ts): a fixed sets × reps; hit every rep on every set
// and the weight goes up next time; miss twice in a row at the same weight and it drops 10% to build back up. The next weight
// is worked out from the last two sessions, so there's no progression state to store or keep in sync.
import type { Units } from './auth.ts';
import { convertWeight, roundWeight, weightStep } from './units.ts';
import type { SetRecord } from './workouts.ts';

/** How much a deload takes off the working weight. */
export const DELOAD = 0.1;

/** A sets × reps target with linear progression (in practice, a weighted exercise in a split's template). */
export type Target = {
  exerciseId: string;
  sets: number;
  reps: number;
  /** Added after a session where every target rep was hit. In `unit`. */
  increment: number;
  unit: Units;
  /** The first session's weight, if there's no history yet. In `unit`. */
  startWeight: number | null;
};

type Sets = Pick<SetRecord, 'weight' | 'unit' | 'reps' | 'warmup' | 'deletedAt'>[];

/**
 * How a session went against the target: its working weight (the heaviest working set, in `units`) and whether
 * at least the target number of sets at that weight reached the target reps.
 */
export function sessionResult(target: Pick<Target, 'sets' | 'reps'>, sets: Sets, units: Units): { weight: number | null; hit: boolean; done: number } {
  const working = sets.filter((s) => !s.warmup && !s.deletedAt && s.weight !== null);
  if (!working.length) return { weight: null, hit: false, done: 0 };
  const kg = (s: Sets[number]) => convertWeight(s.weight!, s.unit, 'kg');
  const top = Math.max(...working.map(kg));
  // Sets at the working weight (within 10 g, so lb/kg round trips match) that reached the target reps.
  const done = working.filter((s) => kg(s) >= top - 0.01 && (s.reps ?? 0) >= target.reps).length;
  return { weight: roundWeight(convertWeight(top, 'kg', units), units), hit: done >= target.sets, done };
}

export type NextTarget = {
  sets: number;
  reps: number;
  /** In the person's units; null when there's no history and no starting weight. */
  weight: number | null;
  basis: 'start' | 'up' | 'same' | 'deload';
  /** Last session's working weight, for "after 140 lb". */
  last: number | null;
};

/** What to aim for next session, from the last two sessions (newest first, deleted and warm-up sets ignored). */
export function nextTarget(target: Target, last: Sets, previous: Sets, units: Units): NextTarget {
  const base = { sets: target.sets, reps: target.reps };
  const now = sessionResult(target, last, units);
  if (now.weight === null) {
    const start = target.startWeight === null ? null : roundWeight(convertWeight(target.startWeight, target.unit, units), units);
    return { ...base, weight: start, basis: 'start', last: null };
  }
  if (now.hit) return { ...base, weight: roundWeight(now.weight + convertWeight(target.increment, target.unit, units), units), basis: 'up', last: now.weight };
  const before = sessionResult(target, previous, units);
  if (before.weight !== null && !before.hit && Math.abs(before.weight - now.weight) < 0.01) {
    // Two misses in a row at the same weight: drop 10%, to a weight you can load.
    const step = weightStep(units);
    return { ...base, weight: Math.round((now.weight * (1 - DELOAD)) / step) * step, basis: 'deload', last: now.weight };
  }
  return { ...base, weight: now.weight, basis: 'same', last: now.weight };
}

/** "Hit 140 lb last time, +5", "Missed twice at 140 lb, so 10% lighter", … */
export function describeNext(n: NextTarget, units: Units): string {
  switch (n.basis) {
    case 'start':
      return n.weight === null ? 'First time: pick a weight you can do for every rep.' : 'Starting weight.';
    case 'up':
      return `Hit every rep at ${n.last} ${units} last time, so up ${+(n.weight! - n.last!).toFixed(2)}.`;
    case 'same':
      return `Didn’t get all ${n.sets} × ${n.reps} at ${n.last} ${units} last time, so the same again.`;
    case 'deload':
      return `Missed twice at ${n.last} ${units}, so 10% lighter to build back up.`;
  }
}
